import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import type { RecordSourceSelectorProxy } from 'relay-runtime';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import type { CropProperties } from '@atlassian/jira-navigation-apps-sidebar-inline-config-project-header/src/common/types.tsx';
import { useAvatars } from '@atlassian/jira-navigation-apps-sidebar-inline-config-project-header/src/services/use-avatars/index.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { updateAvatarsMutation } from '@atlassian/jira-relay/src/__generated__/updateAvatarsMutation.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import RelayDataID from '@atlassian/relay-data-id';

export const useUpdateAvatars = (projectId: number, selectedAvatarUrl: string) => {
	const cloudId = useCloudId();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const {
		selectedAvatar,
		systemAvatars,
		customAvatars,
		latestCustomAvatar,
		setLatestCustomAvatar,
		uploadCustomerIcon,
	} = useAvatars(projectId, selectedAvatarUrl);

	const [commitMutation] = useMutation<updateAvatarsMutation>(graphql`
		mutation updateAvatarsMutation($avatarId: ID!, $cloudId: ID!, $projectIdOrKey: String!) {
			jira {
				updateProjectAvatar(
					input: { avatarId: $avatarId, cloudId: $cloudId, projectIdOrKey: $projectIdOrKey }
				) {
					success
					errors {
						message
						extensions {
							statusCode
							errorType
						}
					}
					project {
						id
						name
					}
				}
			}
		}
	`);

	const getAvatarUrlFromId = useCallback(
		(newAvatarId: number) => {
			const url = selectedAvatarUrl.substring(0, selectedAvatarUrl.lastIndexOf('/'));

			return `${url}/${String(newAvatarId)}`;
		},
		[selectedAvatarUrl],
	);

	const updateProjectAvatar = useCallback(
		(avatarId: number, avatarUrl: string, onError?: (errorState: boolean) => void) => {
			if (!projectId || (selectedAvatar && avatarUrl === selectedAvatar.dataURI)) {
				return;
			}
			const avatarUpdater = (store: RecordSourceSelectorProxy) => {
				const ari = JiraProjectAri.create({
					siteId: cloudId,
					projectId: String(projectId),
				});
				const projectRelayDataId = RelayDataID({ id: ari }, 'JiraProject');
				if (!projectRelayDataId) return;

				const project = store.get(projectRelayDataId);
				const avatarRelayDataId = RelayDataID({ id: ari }, 'JiraAvatar');

				if (project && avatarRelayDataId) {
					let avatar = store.get(avatarRelayDataId);
					if (!avatar) {
						avatar = store.create(avatarRelayDataId, 'JiraAvatar');
					}
					avatar.setValue(avatarRelayDataId, 'id');
					avatar.setValue(getAvatarUrlFromId(avatarId), 'large');
					project.setLinkedRecord(avatar, 'avatar');
				}
			};
			commitMutation({
				variables: {
					avatarId: String(avatarId),
					cloudId,
					projectIdOrKey: String(projectId),
				},
				optimisticUpdater: avatarUpdater,
				updater: (store, response) => {
					if (response?.jira?.updateProjectAvatar?.success) {
						avatarUpdater(store);
					}
				},
				onCompleted: (response) => {
					const { success, errors } = response?.jira?.updateProjectAvatar ?? {};
					const error = errors?.find((e) => e.message);

					if (success) {
						fireTrackAnalytics(createAnalyticsEvent({}), 'projectAvatarUpdate succeeded');
					} else if (error) {
						onError?.(true);
						fireTrackAnalytics(createAnalyticsEvent({}), 'projectAvatarUpdate failed', {
							error,
						});
					}
				},
				onError: (error) => {
					onError?.(true);
					fireTrackAnalytics(createAnalyticsEvent({}), 'projectAvatarUpdate failed', {
						error,
					});
				},
			});
		},
		[projectId, selectedAvatar, commitMutation, cloudId, getAvatarUrlFromId, createAnalyticsEvent],
	);

	const uploadProjectAvatar = useCallback(
		(file: File, crop: CropProperties, onError?: (errorState: boolean) => void): Promise<void> => {
			return uploadCustomerIcon(projectId, file, crop).then((data) => {
				const url = getAvatarUrlFromId(data.id);
				updateProjectAvatar(data.id, url, onError);
				setLatestCustomAvatar({
					id: Number(data.id),
					dataURI: url,
					name: '',
				});
			});
		},
		[uploadCustomerIcon, projectId, updateProjectAvatar, getAvatarUrlFromId, setLatestCustomAvatar],
	);

	return {
		updateProjectAvatar,
		systemAvatars,
		customAvatars,
		selectedAvatar,
		uploadProjectAvatar,
		latestCustomAvatar,
	};
};
